import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../contexts/AuthContext';
import { ConsultantType, BusinessConsultantChat, BusinessConsultantMessage } from '../types/businessConsultant';
import { businessConsultantService } from '../services/businessConsultantService';
import { toast } from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';

// Simplified consultant personas for immediate functionality
const consultantPersonas = {
  marketing: {
    name: 'Keith',
    title: 'Marketing Strategist',
    description: 'Specializing in digital marketing, brand positioning, and customer acquisition strategies.',
    color: '#4F46E5'
  },
  finance: {
    name: 'Carol', 
    title: 'Financial Advisor',
    description: 'Expert in financial planning, funding options, and optimizing business financials.',
    color: '#10B981'
  },
  strategy: {
    name: 'Nikita',
    title: 'Business Strategist', 
    description: 'Specialized in business planning, competitive analysis, and growth strategies.',
    color: '#F59E0B'
  }
};

const BusinessConsultant: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [hasConsented, setHasConsented] = useState<boolean>(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [selectedConsultant, setSelectedConsultant] = useState<ConsultantType | null>(null);
  const [chats, setChats] = useState<BusinessConsultantChat[]>([]);
  const [currentChat, setCurrentChat] = useState<BusinessConsultantChat | null>(null);
  const [messages, setMessages] = useState<BusinessConsultantMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [messageInput, setMessageInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  // Initialize component
  useEffect(() => {
    const initializeComponent = async () => {
      setIsLoading(true);
      try {
        // Check consent status
        const consentStatus = await businessConsultantService.getConsentStatus();
        setHasConsented(consentStatus);
        
        if (!consentStatus) {
          setShowTermsModal(true);
        } else {
          // Load user's chats
          const userChats = await businessConsultantService.getChats();
          setChats(userChats);
        }
      } catch (err) {
        console.error('Error initializing Business Consultant:', err);
        setError('Failed to initialize. Please refresh the page.');
      } finally {
        setIsLoading(false);
      }
    };

    if (isAuthenticated) {
      initializeComponent();
    }
  }, [isAuthenticated]);

  // Handle terms acceptance
  const handleAcceptTerms = async () => {
    try {
      await businessConsultantService.recordConsent(true);
      setHasConsented(true);
      setShowTermsModal(false);
      toast.success('You can now use the Virtual Business Consultants!');
      
      // Load chats after consent
      const userChats = await businessConsultantService.getChats();
      setChats(userChats);
    } catch (err) {
      console.error('Error recording consent:', err);
      toast.error('Failed to record consent. Please try again.');
    }
  };

  // Handle terms rejection
  const handleRejectTerms = () => {
    setShowTermsModal(false);
    toast.error('You must accept the terms to use the Virtual Business Consultants.');
  };

  // Create new chat
  const createNewChat = async (consultantType: ConsultantType) => {
    try {
      setIsLoading(true);
      const chatName = `${consultantPersonas[consultantType].name} Chat`;
      
      const newChat = await businessConsultantService.createChat({
        chat_name: chatName,
        consultant_type: consultantType
      });
      
      setCurrentChat(newChat);
      setSelectedConsultant(consultantType);
      setChats(prev => [newChat, ...prev]);
      
      // Load initial messages
      const chatMessages = await businessConsultantService.getMessagesByChatId(newChat.id);
      setMessages(chatMessages);
      
      toast.success(`Started new chat with ${consultantPersonas[consultantType].name}!`);
    } catch (err) {
      console.error('Error creating chat:', err);
      toast.error('Failed to create new chat. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Send message
  const sendMessage = async () => {
    if (!messageInput.trim() || !currentChat) return;

    const userMessage = messageInput.trim();
    setMessageInput('');
    setIsTyping(true);

    try {
      // Add user message to UI immediately
      const tempUserMessage: BusinessConsultantMessage = {
        id: `temp-${Date.now()}`,
        chat_id: currentChat.id,
        role: 'user',
        content: userMessage,
        tokens_used: 0,
        created_at: new Date().toISOString()
      };
      setMessages(prev => [...prev, tempUserMessage]);

      // Send message and get AI response
      const aiResponse = await businessConsultantService.sendMessageToChatAndGetResponse(
        currentChat.id,
        userMessage,
        false
      );

      // Reload all messages to get the complete conversation
      const updatedMessages = await businessConsultantService.getMessagesByChatId(currentChat.id);
      setMessages(updatedMessages);

      toast.success('Message sent successfully!');
    } catch (err) {
      console.error('Error sending message:', err);
      toast.error('Failed to send message. Please try again.');
      // Remove temporary message on error
      setMessages(prev => prev.filter(msg => !msg.id.startsWith('temp-')));
    } finally {
      setIsTyping(false);
    }
  };

  // Handle key press
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Display auth status and errors for debugging
  const [debugInfo, setDebugInfo] = useState<any>({});
  
  useEffect(() => {
    // Only run in production to help debug the issue
    if (window.location.hostname.includes('explanatum.co.uk')) {
      const getDebugInfo = async () => {
        try {
          const { data } = await supabase.auth.getSession();
          const userId = data?.session?.user?.id;
          const isAuthValid = !!userId && !!data?.session?.access_token;
          
          setDebugInfo({
            isAuthenticated,
            hasValidSession: !!data?.session,
            hasUserId: !!userId,
            isAuthValid,
            timestamp: new Date().toISOString()
          });
          
          console.log('Auth debug info:', {
            isAuthenticated,
            userId,
            hasValidSession: !!data?.session,
            isAuthValid
          });
        } catch (err) {
          console.error('Error getting debug info:', err);
          setDebugInfo({ error: String(err) });
        }
      };
      
      getDebugInfo();
    }
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Authentication Required</h2>
          <p className="text-gray-600 mb-6">You need to be logged in to access the Business Consultant.</p>
          <Link to="/login" className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700">
            Login
          </Link>
          
          {/* Debug info in production */}
          {Object.keys(debugInfo).length > 0 && (
            <div className="mt-6 p-3 border border-red-300 bg-red-50 rounded text-left text-sm">
              <h3 className="font-bold mb-1">Debug Info:</h3>
              <pre>{JSON.stringify(debugInfo, null, 2)}</pre>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (isLoading && !currentChat) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>Loading Business Consultant...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Virtual Business Consultant - Explanatum</title>
        <meta name="description" content="Get expert business advice from AI-powered consultants specializing in marketing, finance, and strategy." />
      </Helmet>

      {/* Terms Modal */}
      {showTermsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4">
            <h3 className="text-lg font-bold mb-4">Terms and Conditions</h3>
            <div className="text-sm text-gray-600 mb-6">
              <p className="mb-3">
                <strong>Important:</strong> The AI business consultants provide general guidance and should not replace professional consultation.
              </p>
              <p className="mb-3">
                By using this service, you understand that:
              </p>
              <ul className="list-disc list-inside space-y-1 mb-3">
                <li>AI advice may not be fully accurate</li>
                <li>You should verify information before making business decisions</li>
                <li>Professional consultation is recommended for critical decisions</li>
              </ul>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleAcceptTerms}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700"
              >
                Accept & Continue
              </button>
              <button
                onClick={handleRejectTerms}
                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-400"
              >
                Decline
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Virtual Business Consultant</h1>
          <p className="text-gray-600">Get expert advice from our AI-powered business consultants</p>
        </div>

        {/* AI Disclaimer */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Important Disclaimer</h3>
              <p className="mt-1 text-sm text-yellow-700">
                AI advice may not be fully accurate and should not replace professional consultation. Please verify any information before making business decisions.
              </p>
            </div>
          </div>
        </div>

        {!hasConsented ? (
          <div className="text-center py-12">
            <p className="text-gray-600">Please accept the terms and conditions to continue.</p>
          </div>
        ) : !currentChat ? (
          /* Consultant Selection */
          <div className="grid md:grid-cols-3 gap-6">
            {Object.entries(consultantPersonas).map(([type, persona]) => (
              <div key={type} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
                <div className="text-center">
                  <div 
                    className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center text-white text-2xl font-bold"
                    style={{ backgroundColor: persona.color }}
                  >
                    {persona.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <h3 className="text-xl font-bold mb-2">{persona.name}</h3>
                  <p className="text-sm text-gray-600 mb-4">{persona.title}</p>
                  <p className="text-sm text-gray-500 mb-6">{persona.description}</p>
                  <button
                    onClick={() => createNewChat(type as ConsultantType)}
                    disabled={isLoading}
                    className="w-full bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 disabled:opacity-50"
                  >
                    {isLoading ? 'Creating...' : 'Start Chat'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Chat Interface */
          <div className="bg-white rounded-lg shadow-md">
            {/* Chat Header */}
            <div className="border-b p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold mr-3"
                    style={{ backgroundColor: consultantPersonas[currentChat.consultant_type].color }}
                  >
                    {consultantPersonas[currentChat.consultant_type].name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div>
                    <h3 className="font-bold">{consultantPersonas[currentChat.consultant_type].name}</h3>
                    <p className="text-sm text-gray-600">{consultantPersonas[currentChat.consultant_type].title}</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setCurrentChat(null);
                    setMessages([]);
                    setSelectedConsultant(null);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ← Back
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="h-96 overflow-y-auto p-4 space-y-4">
              {messages.map((message) => (
                <div key={message.id} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-[80%] p-3 rounded-lg ${
                      message.role === 'user'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    <div className="whitespace-pre-wrap">{message.content}</div>
                    {message.tokens_used > 0 && (
                      <div className="text-xs mt-1 opacity-60">
                        Tokens: {message.tokens_used}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {isTyping && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 p-3 rounded-lg">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-500 rounded-full animate-pulse"></div>
                      <div className="w-2 h-2 bg-gray-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                      <div className="w-2 h-2 bg-gray-500 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Message Input */}
            <div className="border-t p-4">
              <div className="flex space-x-2">
                <textarea
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your business question..."
                  className="flex-1 p-2 border border-gray-300 rounded resize-none"
                  rows={2}
                />
                <button
                  onClick={sendMessage}
                  disabled={!messageInput.trim() || isTyping}
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  Send
                </button>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessConsultant;
